import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { publicAPI } from "../api/publicAPI";
import { heroData } from "../mock"; // Fallback

const Hero = () => {
  const { t, currentLanguage } = useLanguage();
  const [heroContent, setHeroContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHeroContent();
  }, [currentLanguage]);

  const loadHeroContent = async () => {
    try {
      setLoading(true);
      const content = await publicAPI.getPageContent('hero', currentLanguage);
      setHeroContent(content);
    } catch (err) {
      console.error("Failed to load hero content from API, using fallback:", err);
      setHeroContent(null); // Will use translation keys as fallback
    } finally {
      setLoading(false);
    }
  };
  
  const handleConsultationClick = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <section className="relative bg-gradient-to-br from-slate-50 to-white py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-300 rounded w-3/4"></div>
            <div className="h-6 bg-gray-300 rounded w-1/2"></div>
            <div className="h-10 bg-gray-300 rounded w-1/4"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-gradient-to-br from-slate-50 to-white py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left column - Main content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight">
                {heroContent?.title || t('heroTitle')}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {heroContent?.subtitle || t('heroSubtitle')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-orange-500 hover:bg-orange-600 px-8 py-3 text-lg"
                onClick={handleConsultationClick}
              >
                {t('getConsultation')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center space-x-8 pt-8 border-t border-gray-100">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">20+</div>
                <div className="text-sm text-gray-600">Видов документов</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">100%</div>
                <div className="text-sm text-gray-600">Успешных кейсов</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">24/7</div>
                <div className="text-sm text-gray-600">Поддержка</div>
              </div>
            </div>
          </div>

          {/* Right column - Features card */}
          <div className="lg:pl-8">
            <Card className="p-8 shadow-xl border-0 bg-white">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                {t('ourAdvantages')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 leading-relaxed">{t('technicalAnalysis')}</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 leading-relaxed">{t('instantLaunch')}</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 leading-relaxed">{t('turnkeyProcessing')}</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 leading-relaxed">{t('staffTranslator')}</span>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 font-medium">
                  🎯 {t('workWithAuthentic')}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {t('legalProtection')}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 -z-10 h-full w-1/2 bg-gradient-to-l from-orange-50 to-transparent opacity-30"></div>
    </section>
  );
};

export default Hero;